import React, { useState, useEffect } from "react";
import api from "../../api";
import "../../styles/GameUIStyles.css";
import DOMPurify from "dompurify";
import {
  Col,
  Row,
  Container,
} from "reactstrap";
import { FaPaperPlane, FaClock, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useLocation } from "react-router-dom";

const SendMessageProfile = () => {
  document.title = "Send Message | Pokemon Radiance";

  const location = useLocation();
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [message, setMessage] = useState({ text: "", type: "" });
  const [cooldown, setCooldown] = useState(0);

  // On component mount, check for query parameter "to" and set the recipient.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const toParam = params.get("user");
    if (toParam) {
      setRecipient(toParam);
    }
  }, [location.search]);

  // Validate and sanitize inputs
  const validateInputs = () => {
    // Check for empty fields
    if (!recipient.trim()) {
      setMessage({ text: "Recipient username is missing", type: "error" });
      return false;
    }
    
    if (!subject.trim()) {
      setMessage({ text: "Please enter a subject", type: "error" });
      return false;
    }
    
    if (!content.trim()) {
      setMessage({ text: "Please enter a message", type: "error" });
      return false;
    }
    
    // Check length limits
    if (subject.length > 50) {
      setMessage({ text: "Subject must be 50 characters or less", type: "error" });
      return false;
    }
    
    if (content.length > 2000) {
      setMessage({ text: "Message must be 2000 characters or less", type: "error" });
      return false;
    }
    
    return true;
  };

  const sendMessage = async () => {
    if (cooldown > 0) {
      setMessage({
        text: `Please wait ${cooldown} seconds before sending another message.`,
        type: "error",
      });
      return;
    }
    
    // Validate inputs first
    if (!validateInputs()) {
      return;
    }

    try {
      // Sanitize inputs before sending
      const sanitizedRecipient = DOMPurify.sanitize(recipient.trim());
      const sanitizedSubject = DOMPurify.sanitize(subject.trim());
      const sanitizedContent = DOMPurify.sanitize(content.trim());
      
      const response = await api.post(
        "/v1/messages/send",
        { 
          recipient: sanitizedRecipient, 
          subject: sanitizedSubject, 
          content: sanitizedContent 
        },
        { withCredentials: true },
      );

      setMessage({ text: response.data.message, type: "success" });
      setSubject("");
      setContent("");

      // Start cooldown (60 seconds)
      setCooldown(60);
      const interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      setMessage({
        text: error.response?.data.detail || "Failed to send message.",
        type: "error",
      });
    }
  };

  return (
    <React.Fragment>
      <div className="page-content" style={{ paddingTop: "10px" }}>
        <Container fluid>
          <Breadcrumbs title={recipient} breadcrumbItem="Send Message" />

          <Row className="mb-4">
            <Col xl={12}>
              <div className="game-card">
                <div className="game-card-header">
                  <h5 className="game-card-title">Send Message to {recipient}</h5>
                </div>
                <div className="game-card-body">
                  {message.text && (
                    <div className={`game-alert ${message.type === "success" ? "game-alert-success" : "game-alert-danger"} mb-4`}>
                      {message.type === "success" ? (
                        <FaCheckCircle className="me-2" />
                      ) : (
                        <FaExclamationCircle className="me-2" />
                      )}
                      {message.text}
                    </div>
                  )}
                  
                  <div className="game-input-group">
                    <label className="game-input-label">Subject</label>
                    <input
                      type="text"
                      className="game-input"
                      placeholder="Enter subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      maxLength={50}
                    />
                    <div className="text-secondary mt-1" style={{fontSize: '0.8rem', textAlign: 'right'}}>
                      {subject.length}/50 characters
                    </div>
                  </div>
                  
                  <div className="game-input-group">
                    <label className="game-input-label">Message</label>
                    <textarea
                      className="game-input"
                      style={{
                        minHeight: '200px',
                        resize: 'vertical',
                        padding: '12px',
                        lineHeight: '1.5',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                      }}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Start typing your message here..."
                      maxLength={2000}
                    />
                    <div className="text-secondary mt-1" style={{fontSize: '0.8rem', textAlign: 'right'}}>
                      {content.length} characters
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-end mt-4">
                    <button
                      className="game-button"
                      onClick={sendMessage}
                      disabled={cooldown > 0}
                    >
                      <span>Send Message</span>
                      <FaPaperPlane className="game-button-icon" />
                    </button>
                  </div>
                  
                  {cooldown > 0 && (
                    <div className="mt-3 d-flex align-items-center justify-content-end text-warning">
                      <FaClock className="me-2" />
                      <span>You can send another message in {cooldown} seconds</span>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SendMessageProfile;
